import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Chip,
  CircularProgress,
  Divider,
  Alert,
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { FileText, RefreshCw, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import { Project } from '../../types';

interface AnalyticsViewProps {
  projects: Project[];
}

/* ── helpers ── */
const STATUS_COLORS: Record<string, string> = {
  'In Progress': '#3b82f6',
  Active: '#3b82f6',
  Completed: '#22c55e',
  'On Hold': '#f59e0b',
};

function fmtZMW(val: number) {
  if (val >= 1_000_000) return `ZMW ${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `ZMW ${(val / 1_000).toFixed(0)}K`;
  return `ZMW ${val.toLocaleString()}`;
}

/* ── simple markdown renderer ── */
function MarkdownReport({ text }: { text: string }) {
  const lines = text.split('\n');
  return (
    <Box>
      {lines.map((line, i) => {
        if (/^#{1,3} /.test(line)) {
          const content = line.replace(/^#+\s/, '');
          return (
            <Typography key={i} variant="subtitle1" fontWeight={700} mt={2} mb={0.5} color="text.primary">
              {content}
            </Typography>
          );
        }
        if (/^\*\*(.+)\*\*$/.test(line)) {
          return (
            <Typography key={i} variant="body2" fontWeight={700} mt={1.5}>
              {line.replace(/\*\*/g, '')}
            </Typography>
          );
        }
        if (/^- /.test(line) || /^\* /.test(line)) {
          return (
            <Typography key={i} variant="body2" sx={{ pl: 2, lineHeight: 1.8 }} color="text.secondary">
              • {line.replace(/^[-*] /, '')}
            </Typography>
          );
        }
        if (line.trim() === '') return <Box key={i} sx={{ height: 4 }} />;
        return (
          <Typography key={i} variant="body2" color="text.secondary" sx={{ lineHeight: 1.8 }}>
            {line.replace(/\*\*/g, '')}
          </Typography>
        );
      })}
    </Box>
  );
}

export default function AnalyticsView({ projects }: AnalyticsViewProps) {
  const [report, setReport] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Check if running in development (localhost)
  const isDev = typeof window !== 'undefined' && window.location.hostname === 'localhost';

  /* ── derived stats ── */
  const stats = useMemo(() => {
    const total = projects.length;
    const active = projects.filter((p) => p.status === 'In Progress' || p.status === 'Active').length;
    const completed = projects.filter((p) => p.status === 'Completed').length;
    const onHold = projects.filter((p) => p.status === 'On Hold').length;
    const totalBudget = projects.reduce((s, p) => s + (p.budgetedAmount || 0), 0);
    const totalSpent = projects.reduce((s, p) => s + (p.spentAmount || 0), 0);
    const overBudget = projects.filter((p) => (p.spentAmount || 0) > (p.budgetedAmount || 0)).length;
    const avgProgress =
      total > 0 ? Math.round(projects.reduce((s, p) => s + (p.progress || 0), 0) / total) : 0;
    return { total, active, completed, onHold, totalBudget, totalSpent, overBudget, avgProgress };
  }, [projects]);

  /* ── chart data ── */
  const statusData = useMemo(() => {
    const counts: Record<string, number> = {};
    projects.forEach((p) => {
      const key = p.status === 'Active' ? 'In Progress' : p.status;
      counts[key] = (counts[key] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [projects]);

  const budgetData = useMemo(
    () =>
      projects.map((p) => ({
        name: p.name.length > 14 ? p.name.slice(0, 13) + '…' : p.name,
        Budget: p.budgetedAmount || 0,
        Spent: p.spentAmount || 0,
      })),
    [projects]
  );

  const progressData = useMemo(
    () =>
      projects.map((p) => ({
        name: p.name.length > 14 ? p.name.slice(0, 13) + '…' : p.name,
        Progress: p.progress || 0,
      })),
    [projects]
  );

  /* ── AI report ── */
  async function generateReport() {
    setLoading(true);
    setError(null);
    setReport(null);
    try {
      // In development, use the deployed Pages Function endpoint
      const apiUrl = isDev 
        ? 'https://project-manager-3up.pages.dev/api/report'
        : '/api/report';
      
      const res = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projects }),
      });
      
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        throw new Error(`Invalid response from server (HTTP ${res.status}): ${text.substring(0, 200)}`);
      }
      
      if (!res.ok) throw new Error(data.error || 'Unknown error');
      setReport(data.report);
    } catch (e: any) {
      setError(e.message || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  }

  const utilPct = stats.totalBudget > 0 ? Math.round((stats.totalSpent / stats.totalBudget) * 100) : 0;
  const utilColor = utilPct > 90 ? 'error' : utilPct > 70 ? 'warning' : 'success';

  return (
    <Box
      sx={{
        height: 'calc(100vh - 90px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        px: 3,
        pt: 1,
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexShrink: 0 }}>
        <Typography variant="h6" fontWeight={700}>
          Analytics &amp; Reports
        </Typography>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FileText size={16} />}
          onClick={generateReport}
          disabled={loading || projects.length === 0}
          title={isDev ? 'Uses deployed AI endpoint' : undefined}
          sx={{
            backgroundColor: '#C9A84C',
            color: '#000',
            fontWeight: 600,
            textTransform: 'none',
            '&:hover': { backgroundColor: '#b8983e' },
          }}
        >
          {loading ? 'Generating…' : 'Generate AI Report'}
        </Button>
      </Box>

      {/* ── Scrollable body ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0, pb: 3 }}>
        {/* KPI strip */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {[
            { label: 'Total Projects', value: stats.total, color: '#3b82f6' },
            { label: 'Active', value: stats.active, color: '#3b82f6' },
            { label: 'Completed', value: stats.completed, color: '#22c55e' },
            { label: 'On Hold', value: stats.onHold, color: '#f59e0b' },
            { label: 'Over Budget', value: stats.overBudget, color: '#ef4444' },
            { label: 'Avg Progress', value: `${stats.avgProgress}%`, color: '#a855f7' },
          ].map((k) => (
            <Grid size={{ xs: 6, sm: 4, md: 2 }} key={k.label}>
              <Card variant="outlined" sx={{ p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {k.label}
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: k.color }}>
                  {k.value}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Budget summary strip */}
        <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap', mb: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">Total Budget</Typography>
              <Typography variant="subtitle1" fontWeight={700}>{fmtZMW(stats.totalBudget)}</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary">Spent</Typography>
              <Typography variant="subtitle1" fontWeight={700}>{fmtZMW(stats.totalSpent)}</Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography variant="caption" color="text.secondary">Remaining</Typography>
              <Typography variant="subtitle1" fontWeight={700} color="success.main">
                {fmtZMW(Math.max(0, stats.totalBudget - stats.totalSpent))}
              </Typography>
            </Box>
            <Box sx={{ flexGrow: 1, minWidth: 120 }}>
              <Typography variant="caption" color="text.secondary">{utilPct}% utilised</Typography>
              <LinearProgress variant="determinate" value={Math.min(utilPct, 100)} color={utilColor} sx={{ height: 8, borderRadius: 4, mt: 0.5 }} />
            </Box>
          </Box>
        </Card>

        {/* Charts row */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Status pie */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined" sx={{ height: 280 }}>
              <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                  Status Breakdown
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={statusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => `${name} (${value})`} labelLine={false}>
                        {statusData.map((entry, index) => (
                          <Cell key={index} fill={STATUS_COLORS[entry.name] || '#8b5cf6'} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Progress bars */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined" sx={{ height: 280 }}>
              <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                  Project Progress
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={progressData} layout="vertical" margin={{ left: 0, right: 16, top: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={80} />
                      <Tooltip formatter={(v: number) => `${v}%`} />
                      <Bar dataKey="Progress" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Budget vs Spent */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Card variant="outlined" sx={{ height: 280 }}>
              <CardContent sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle2" fontWeight={700} mb={1}>
                  Budget vs Spent
                </Typography>
                <Box sx={{ flex: 1 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={budgetData} margin={{ left: 0, right: 8, top: 4, bottom: 4 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="name" tick={{ fontSize: 9 }} />
                      <YAxis tickFormatter={(v) => fmtZMW(v)} tick={{ fontSize: 9 }} width={60} />
                      <Tooltip formatter={(v: number) => fmtZMW(v)} />
                      <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="Budget" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="Spent" fill="#C9A84C" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Per-project health table */}
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
              Project Health Overview
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                <Box component="thead">
                  <Box component="tr" sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                    {['Project', 'Status', 'Progress', 'Budget', 'Spent', 'Utilisation', 'Health'].map((h) => (
                      <Box component="th" key={h} sx={{ py: 0.75, px: 1, textAlign: 'left', fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {h}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {projects.map((p) => {
                    const util = p.budgetedAmount ? Math.round((p.spentAmount / p.budgetedAmount) * 100) : 0;
                    const isOver = (p.spentAmount || 0) > (p.budgetedAmount || 0);
                    const healthColor = isOver ? 'error' : util > 85 ? 'warning' : 'success';
                    const HealthIcon = isOver ? AlertTriangle : CheckCircle;
                    return (
                      <Box component="tr" key={p.id} sx={{ borderBottom: '1px solid', borderColor: 'divider', '&:last-child': { borderBottom: 'none' } }}>
                        <Box component="td" sx={{ py: 0.75, px: 1, fontWeight: 500 }}>{p.name}</Box>
                        <Box component="td" sx={{ py: 0.75, px: 1 }}>
                          <Chip label={p.status} size="small" sx={{ fontSize: '0.68rem', height: 18, bgcolor: STATUS_COLORS[p.status] || '#8b5cf6', color: '#fff' }} />
                        </Box>
                        <Box component="td" sx={{ py: 0.75, px: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress variant="determinate" value={p.progress || 0} sx={{ flex: 1, height: 6, borderRadius: 3 }} />
                            <span>{p.progress || 0}%</span>
                          </Box>
                        </Box>
                        <Box component="td" sx={{ py: 0.75, px: 1, whiteSpace: 'nowrap' }}>{fmtZMW(p.budgetedAmount || 0)}</Box>
                        <Box component="td" sx={{ py: 0.75, px: 1, whiteSpace: 'nowrap' }}>{fmtZMW(p.spentAmount || 0)}</Box>
                        <Box component="td" sx={{ py: 0.75, px: 1, whiteSpace: 'nowrap' }}>{util}%</Box>
                        <Box component="td" sx={{ py: 0.75, px: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <HealthIcon size={14} color={healthColor === 'error' ? '#ef4444' : healthColor === 'warning' ? '#f59e0b' : '#22c55e'} />
                            <Typography variant="caption" color={`${healthColor}.main`} fontWeight={600}>
                              {isOver ? 'Over Budget' : util > 85 ? 'Watch' : 'On Track'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* AI Report panel */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {(loading || report) && (
          <Card variant="outlined">
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                <TrendingUp size={18} />
                <Typography variant="subtitle2" fontWeight={700}>
                  AI Executive Summary
                </Typography>
                {report && !loading && (
                  <Button
                    size="small"
                    startIcon={<RefreshCw size={13} />}
                    onClick={generateReport}
                    sx={{ ml: 'auto', textTransform: 'none', fontSize: '0.75rem' }}
                  >
                    Regenerate
                  </Button>
                )}
              </Box>
              <Divider sx={{ mb: 1.5 }} />
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3, justifyContent: 'center' }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="text.secondary">
                    Generating executive summary with Cloudflare AI…
                  </Typography>
                </Box>
              ) : (
                report && <MarkdownReport text={report} />
              )}
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
}
